import React from "react"

import { Flyout } from "./Layout"
import Typography from "./Typography"

export type NotificationDeliveryLogDetail = {
  body?: string | null
  bodyRedacted?: boolean
  createdAt: string
  deliveryChannel: string
  durationMs?: number | null
  enqueuedAt?: string | null
  errorCode?: string | null
  errorMessage?: string | null
  eventType: string
  finishedAt?: string | null
  fromAddress?: string | null
  startedAt?: string | null
  status: string
  subject?: string | null
  toAddress?: string | null
}

type Props = {
  log: NotificationDeliveryLogDetail | null
  visible: boolean
  onClose: () => void
  formatLocal: (iso: string | null | undefined) => string
}

function DetailBlock({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  if (children === null || children === undefined || children === "") {
    return null
  }
  return (
    <div className="border-b border-gray-light py-3 last:border-b-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-dark">{label}</dt>
      <dd className="mt-1 break-words text-sm">{children}</dd>
    </div>
  )
}

export default function NotificationDeliveryDetailsFlyout({ log, visible, onClose, formatLocal }: Props) {
  if (!log) {
    return null
  }

  const header = `Delivery details`

  return (
    <Flyout
      header={header}
      visible={visible}
      closeFlyout={onClose}
      compactHeader
      footerSticky={false}
      footer={undefined}>
      <dl>
        <DetailBlock label="Notification type">{log.eventType}</DetailBlock>
        <DetailBlock label="Channel">{log.deliveryChannel}</DetailBlock>
        <DetailBlock label="Delivery status">{log.status}</DetailBlock>

        <div className="mt-6">
          <Typography variant="h4" as="h3" className="mb-2 text-base">
            Timeline (your local time)
          </Typography>
          <DetailBlock label="Enqueued">{formatLocal(log.enqueuedAt)}</DetailBlock>
          <DetailBlock label="Started">{formatLocal(log.startedAt)}</DetailBlock>
          <DetailBlock label="Finished">{formatLocal(log.finishedAt)}</DetailBlock>
          <DetailBlock label="Logged at">{formatLocal(log.createdAt)}</DetailBlock>
          <DetailBlock label="Duration">
            {log.durationMs != null ? `${log.durationMs} ms` : null}
          </DetailBlock>
        </div>

        <div className="mt-6">
          <Typography variant="h4" as="h3" className="mb-2 text-base">
            Addresses & content
          </Typography>
          <DetailBlock label="From">{log.fromAddress}</DetailBlock>
          <DetailBlock label="To">{log.toAddress}</DetailBlock>
          <DetailBlock label="Subject">{log.subject}</DetailBlock>
          {(log.body || log.bodyRedacted) && (
            <DetailBlock label="Body (may be redacted)">
              <>
                {log.body ? (
                  <span className="whitespace-pre-wrap font-mono text-xs">{log.body}</span>
                ) : null}
                {log.bodyRedacted ? (
                  <span className="mt-1 block text-xs text-gray-dark">Marked as redacted in storage.</span>
                ) : null}
              </>
            </DetailBlock>
          )}
        </div>

        <div className="mt-6">
          <Typography variant="h4" as="h3" className="mb-2 text-base">
            Errors
          </Typography>
          <DetailBlock label="Error code">{log.errorCode}</DetailBlock>
          <DetailBlock label="Error message">
            {log.errorMessage ? (
              <span className="whitespace-pre-wrap break-words font-mono text-xs">{log.errorMessage}</span>
            ) : null}
          </DetailBlock>
        </div>

        <div className="mt-8">
          <button
            type="button"
            onClick={onClose}
            className="h-8 rounded border border-gray bg-white px-4 font-bold focus:outline-none focus:ring-2 focus:ring-blue active:bg-gray-light">
            Close
          </button>
        </div>
      </dl>
    </Flyout>
  )
}
